import React from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Code, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { CATEGORIES, PLACEHOLDER_HINTS } from './documentTemplateConstants';

const DocumentTemplateEditorDialog = ({
  open, onOpenChange,
  currentTemplate, formData, setFormData,
  handleSave, handleDuplicate,
  showVariables, setShowVariables,
}) => {
  const { t } = useTranslation();
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{currentTemplate ? t('docComp.editTitle') : t('docComp.newTitle')}</DialogTitle>
        <DialogDescription>
          {currentTemplate?.is_default
            ? t('docComp.defaultDescYes')
            : t('docComp.defaultDescNo')}
        </DialogDescription>
      </DialogHeader>

      {currentTemplate?.is_default ? (
        <div className="text-center py-8 text-muted-foreground">
          <Settings className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <p className="mb-4">{t('docComp.cannotEdit')}</p>
          <Button onClick={() => handleDuplicate(currentTemplate)}>
            <Copy className="h-4 w-4 mr-2" />
            {t('docComp.duplicate')}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="name">{t('docComp.nameLabel')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('docComp.namePlaceholder')}
              />
            </div>

            <div>
              <Label htmlFor="category">{t('docComp.categoryLabel')}</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{t(cat.labelKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 rounded border-border"
              />
              <Label htmlFor="is_active" className="cursor-pointer">{t('docComp.activeLabel')}</Label>
            </div>

            <div className="col-span-2">
              <Label htmlFor="description">{t('docComp.descLabel')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('docComp.descPlaceholder')}
                rows={2}
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowVariables(!showVariables)}
              className="mb-2"
            >
              <Code className="h-4 w-4 mr-2" />
              {showVariables ? t('docComp.hideVars') : t('docComp.showVars')}
            </Button>

            {showVariables && (
              <div className="bg-muted/50 rounded-lg p-4 max-h-40 overflow-y-auto text-sm">
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(PLACEHOLDER_HINTS).map(([key, descKey]) => (
                    <div key={key} className="flex items-start gap-2">
                      <code className="text-xs bg-card px-2 py-1 rounded border flex-shrink-0">{`{{ ${key} }}`}</code>
                      <span className="text-muted-foreground text-xs">{t(descKey)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="html_body">{t('docComp.htmlLabel')}</Label>
            <Textarea
              id="html_body"
              value={formData.html_body}
              onChange={(e) => setFormData({ ...formData, html_body: e.target.value })}
              placeholder={t('docComp.htmlPlaceholder')}
              rows={12}
              className="font-mono text-sm"
            />
          </div>

          <div>
            <Label htmlFor="css">{t('docComp.cssLabel')}</Label>
            <Textarea
              id="css"
              value={formData.css}
              onChange={(e) => setFormData({ ...formData, css: e.target.value })}
              placeholder={t('docComp.cssPlaceholder')}
              rows={6}
              className="font-mono text-sm"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave}>{currentTemplate ? t('docComp.update') : t('common.create')}</Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
  );
};

export default DocumentTemplateEditorDialog;
